import React, { useState, useRef, useEffect, createContext, useCallback } from 'react';
import SettingsMenu from './SettingsMenu/SettingsMenu.tsx';
import SettingsDetail from './SettingsContent/SettingsDetail.tsx';
import UnsavedChangesPopup from './SettingsContent/Subcomponents/UnsavedChangesPopup/UnsavedChangesPopup.tsx';
import { useAppStore } from "../../../GlobalStore/GlobalStore.ts";
import { useUnsavedChanges } from './hooks/useUnsavedChanges.ts';

// Create context for unsaved changes and persistent tab state
export const UnsavedChangesContext = createContext<{
    setTabHasUnsavedChanges: (tabName: string, hasChanges: boolean) => void;
}>({
    setTabHasUnsavedChanges: () => {}
});

// Context for preserving tab states across tab switches
export const TabStateContext = createContext<{
    getTabState: (tabName: string) => any;
    setTabState: (tabName: string, state: any) => void;
}>({
    getTabState: () => ({}),
    setTabState: () => {}
});

interface SettingsProps {
    defaultActiveTab?: string;
}

const Settings: React.FC<SettingsProps> = ({ defaultActiveTab }) => {
  const setDynamicStateObj = useAppStore((state: any) => state.setDynamicStateObj);

  const [activeTab, setActiveTab] = useState(defaultActiveTab || 'General');
  const [activeView, setActiveView] = useState(defaultActiveTab ? 'detail' : 'menu');
  const [showUnsavedChangesPopup, setShowUnsavedChangesPopup] = useState(false);

  // Use the unsaved changes hook
  const {
    setTabHasUnsavedChanges,
    hasAnyUnsavedChanges,
    clearAllUnsavedChanges,
    getTabsWithUnsavedChanges
  } = useUnsavedChanges();

  // Store tab states to preserve unsaved changes across tab switches
  const [tabStates, setTabStates] = useState<Record<string, any>>({});

  const getTabState = useCallback((tabName: string) => {
    return tabStates[tabName] || {};
  }, [tabStates]);

  const setTabState = useCallback((tabName: string, state: any) => {
    setTabStates(prev => ({
      ...prev,
      [tabName]: state
    }));
  }, []);

  const modalRef = useRef<any>(null);

  const closeSettings = useCallback(() => {
    // Clear unsaved changes tracking
    clearAllUnsavedChanges();
    // Close the modal
    setDynamicStateObj({visiblePopup: null});
  }, [clearAllUnsavedChanges, setDynamicStateObj]);

  const handleSettingsClose = useCallback(() => {
    // Check if there are unsaved changes
    if (hasAnyUnsavedChanges()) {
      setShowUnsavedChangesPopup(true);
      return;
    }

    // No unsaved changes, safe to close
    closeSettings();
  }, [hasAnyUnsavedChanges, closeSettings]);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
      handleSettingsClose();
    }
  }, [handleSettingsClose]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleSettingsClose();
    }
  }, [handleSettingsClose]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleClickOutside, handleKeyDown]);



  const handleDiscardChanges = () => {
    setShowUnsavedChangesPopup(false);
    closeSettings();
  };

  const handleCancelClose = () => {
    setShowUnsavedChangesPopup(false);
  };

  return (
    <UnsavedChangesContext.Provider value={{ setTabHasUnsavedChanges }}>
      <TabStateContext.Provider value={{ getTabState, setTabState }}>
        <div className="fixed inset-0 bg-neutral-100/75 dark:bg-neutral-900/75 flex items-center justify-center p-4 z-50">
          <div
            ref={modalRef}
            className="bg-neutral-300 dark:bg-neutral-800/80 rounded-xl shadow-2xl flex w-full h-2/3 max-w-4xl overflow-y-scroll border border-neutral-700/50 relative"
          >
            <button
              onClick={handleSettingsClose}
              className="absolute top-2 right-2 dark:text-neutral-200 dark:hover:text-white transition-colors duration-200"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mt-2 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className={`w-full sm:w-1/3 sm:border-r border-neutral-700/30 ${activeView === 'detail' ? 'hidden sm:block' : ''}`}>
              <SettingsMenu
                onTabClick={(tabName) => {
                  setActiveTab(tabName);
                  setActiveView('detail');
                }}
                activeTab={activeTab}
              />
            </div>
            <div className={`w-full sm:w-2/3 ${activeView === 'menu' ? 'hidden sm:block' : 'block'}`}>
              <SettingsDetail
                activeTab={activeTab}
                onBackClick={() => setActiveView('menu')}
              />
            </div>
          </div>

          {showUnsavedChangesPopup && (
            <UnsavedChangesPopup
              tabsWithChanges={getTabsWithUnsavedChanges()}
              onDiscard={handleDiscardChanges}
              onCancel={handleCancelClose}
            />
          )}
        </div>
      </TabStateContext.Provider>
    </UnsavedChangesContext.Provider>
  );
};

export default Settings;